import { Box, Heading, Stack, Text } from "@chakra-ui/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  type ViewDocumentAsOwnerDto,
  useGetBidV2Query,
} from "../../../../../autogen/bff-api";
import { CombinedDocuments } from "../../../../../common/documents/CombinedDocuments";
import {
  type IncompleteFileUpload,
  isIncompleteFileUpload,
  UploadCompletedStatus,
} from "../../../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../../../common/documents/UppyUploader/useUppyUploader";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { isBidV2Editable } from "../publicBidEditability";
import { RoundBidDocumentsNotice } from "../RoundBidDocumentsNotice";

interface Props {
  bidId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const BidDocumentsStep = ({
  bidId,
  previousStep,
  nextStep,
  nextStepLabel,
}: Props) => {
  const { t } = useTranslation();
  const { data: bid, refetch } = useGetBidV2Query({ bidId });
  const readOnly = !bid || !isBidV2Editable(bid);

  const {
    uppy,
    allFiles,
    documents,
    setDocuments,
    removeUpload,
    reloadEntity,
  } = useUppyUploader({
    entityType: "BidV2",
    entityId: bidId,
    reloadEntity: refetch,
  });

  useEffect(() => {
    setDocuments(bid?.documents ?? []);
  }, [bid?.documents, setDocuments]);

  return (
    <Box pb="8" pt="6">
      <Stack spacing="2" mb="6">
        <Heading size="md">{t("Offer documents")}</Heading>
        <Text fontSize="sm" color="smMuted">
          {t(
            "Upload the documents that belong to your offer, such as technical specifications, signed declarations and references.",
          )}
        </Text>
        <RoundBidDocumentsNotice bid={bid} />
      </Stack>

      <Box
        borderWidth="1px"
        borderColor="smBorder"
        bg="smBackground"
        rounded="lg"
        p="3"
        mb="8"
      >
        <CombinedDocuments
          uploadedDocuments={documents}
          setUploadedDocuments={setDocuments}
          removeUploadedDocument={(document: ViewDocumentAsOwnerDto) =>
            removeUpload({
              status: UploadCompletedStatus.UploadCompleted,
              documentId: document.id,
              uploadId: document.fromUploadId,
              ...document,
            })
          }
          isTagsColumnVisible={false}
          isCategoryColumnVisible={false}
          canDeleteDocuments={!readOnly}
          canEditDocumentNames={!readOnly}
          documentUploadConfig={
            readOnly
              ? undefined
              : {
                  uppy,
                  dropTargetId: `bid-documents-${bidId}`,
                  dropTargetHelperText: t(
                    "Drag and drop files here, or click to browse",
                  ),
                  uploads: allFiles.filter(
                    isIncompleteFileUpload,
                  ) as IncompleteFileUpload[],
                  removeUpload,
                }
          }
          refetchDocuments={() => {
            reloadEntity();
          }}
        />
      </Box>

      <StepNavigation
        onPrevious={previousStep}
        onNext={nextStep}
        nextLabel={nextStepLabel}
      />
    </Box>
  );
};
